/**
 * User Registration Screen Component
 *
 * This component handles new user registration with:
 * - Email and password registration
 * - Form validation for all fields
 * - Terms of service and marketing consent options
 * - Social authentication providers
 * - Success confirmation with email verification instructions
 *
 * The registration flow includes validation, duplicate email checking,
 * and Supabase authentication integration.
 */
import type { Route } from "./+types/join";

import { CheckCircle2Icon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Form, Link, data, redirect } from "react-router";
import { z } from "zod";

import FormButton from "~/core/components/form-button";
import FormErrors from "~/core/components/form-error";
import TurnstileWidget from "~/core/components/turnstile-widget";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "~/core/components/ui/alert";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/core/components/ui/card";
import { Checkbox } from "~/core/components/ui/checkbox";
import { Input } from "~/core/components/ui/input";
import { Label } from "~/core/components/ui/label";
import adminClient from "~/core/lib/supa-admin-client.server";
import makeServerClient from "~/core/lib/supa-client.server";
import { getRemoteIp, verifyTurnstileToken } from "~/core/lib/turnstile.server";

import { translateAuthErrors } from "../lib/auth-errors";
import { doesUserExist } from "../lib/queries.server";

/**
 * Meta function for the registration page
 *
 * Sets the page title using the application name from environment variables
 */
export const meta: Route.MetaFunction = () => {
  return [
    {
      title: `Create an account | ${import.meta.env.VITE_APP_NAME}`,
    },
    {
      name: "robots",
      content: "noindex, nofollow",
    },
  ];
};

/**
 * Form validation schema for user registration
 *
 * Uses Zod to validate:
 * - Name: Required field
 * - Email: Must be a valid email format
 * - Password: Must be at least 8 characters long
 * - Confirm Password: Must match the password field
 * - Marketing: Boolean for marketing consent (defaults to false)
 * - Terms: Boolean for terms acceptance
 *
 * The schema includes a custom refinement to ensure passwords match
 */
const joinSchema = z
  .object({
    name: z.string().min(1, { message: "name_required" }),
    email: z.string().email({ message: "invalid_email" }),
    password: z.string().min(8, { message: "password_min" }),
    confirmPassword: z.string().min(8, { message: "password_min" }),
    marketing: z.coerce.boolean().default(false),
    terms: z.coerce.boolean(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "passwords_must_match",
    path: ["confirmPassword"],
  });

/**
 * Server action for handling user registration form submission
 *
 * This function processes the registration form data and attempts to create a new user.
 * The flow is:
 * 1. Parse and validate form data using the join schema
 * 2. Return validation errors if the data is invalid
 * 3. Verify terms of service acceptance
 * 4. Check if a user with the provided email already exists
 * 5. Create a new user with Supabase auth
 * 6. Return success or error response
 *
 * @param request - The form submission request
 * @returns Validation errors, auth errors, or success confirmation
 */
export async function action({ request }: Route.ActionArgs) {
  // Parse form data from the request
  const formData = await request.formData();
  const {
    data: validData,
    success,
    error,
  } = joinSchema.safeParse(Object.fromEntries(formData));

  // Return validation errors if form data is invalid
  if (!success) {
    return data({ fieldErrors: error.flatten().fieldErrors }, { status: 400 });
  }

  // Verify terms of service acceptance
  if (!validData.terms) {
    return data({ error: "must_agree_terms" }, { status: 400 });
  }

  // Verify Cloudflare Turnstile token before any account creation work.
  const turnstileToken = formData.get("cf-turnstile-response");
  const captchaResult = await verifyTurnstileToken(
    typeof turnstileToken === "string" ? turnstileToken : null,
    getRemoteIp(request),
  );
  if (!captchaResult.success) {
    return data({ error: "captcha_failed" }, { status: 400 });
  }

  // Check if a user with the provided email already exists
  const userExists = await doesUserExist(validData.email);

  if (userExists) {
    return data({ error: "email_exists" }, { status: 400 });
  }

  // Temporary local-only switch for short onboarding windows.
  // Production must always require email ownership verification.
  const disableEmailConfirmation =
    process.env.NODE_ENV !== "production" &&
    process.env.DISABLE_EMAIL_CONFIRMATION === "true";
  if (disableEmailConfirmation) {
    const { error: createUserError } = await adminClient.auth.admin.createUser({
      email: validData.email,
      password: validData.password,
      email_confirm: true,
      user_metadata: {
        name: validData.name,
        display_name: validData.name,
        marketing_consent: validData.marketing,
      },
    });

    if (createUserError) {
      return data({ error: createUserError.message }, { status: 400 });
    }

    // Create an authenticated session right away.
    const [client, headers] = makeServerClient(request);
    const { error: immediateSignInError } = await client.auth.signInWithPassword(
      {
        email: validData.email,
        password: validData.password,
      },
    );

    if (immediateSignInError) {
      return data({ error: immediateSignInError.message }, { status: 400 });
    }

    return redirect("/", { headers });
  }

  // Create Supabase client and attempt to sign up the user
  const [client] = makeServerClient(request);
  const { error: signInError } = await client.auth.signUp({
    ...validData,
    options: {
      // Store additional user metadata in Supabase auth
      data: {
        name: validData.name,
        display_name: validData.name,
        marketing_consent: validData.marketing,
      },
    },
  });

  // Return error if user creation fails
  if (signInError) {
    return data({ error: signInError.message }, { status: 400 });
  }

  // Return success response
  return {
    success: true,
  };
}

/**
 * Registration Component
 *
 * This component renders the registration form and handles user interactions.
 * It includes:
 * - Personal information fields (name, email)
 * - Password creation with confirmation
 * - Terms of service and marketing consent checkboxes
 * - Error display for form validation and registration errors
 * - Success confirmation with email verification instructions
 * - Social registration options
 * - Sign in link for existing users
 *
 * @param actionData - Data returned from the form action, including errors or success status
 */
export default function Join({ actionData }: Route.ComponentProps) {
  const { t } = useTranslation();
  // Reference to the form element for resetting after successful submission
  const formRef = useRef<HTMLFormElement>(null);
  // Track Turnstile token so we can disable submit until the user passes the challenge.
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  // Reset the form when registration is successful
  useEffect(() => {
    if (actionData && "success" in actionData && actionData.success) {
      formRef.current?.reset();
      formRef.current?.blur();
      setCaptchaToken(null);
    }
  }, [actionData]);
  return (
    <div className="flex w-full min-w-0 flex-col items-center justify-center gap-4 px-1 sm:px-3">
      <Card className="w-full max-w-md overflow-hidden">
        <CardHeader className="flex flex-col items-center">
          <CardTitle className="text-2xl font-semibold" role="heading">
            {t("authPage.joinTitle")}
          </CardTitle>
          <CardDescription className="text-base">
            {t("authPage.joinDescription")}
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 px-3 sm:px-6">
          <Form
            className="flex w-full min-w-0 flex-col gap-5"
            method="post"
            ref={formRef}
          >
            <div className="flex flex-col items-start space-y-2">
              <Label htmlFor="name" className="flex flex-col items-start gap-1">
                {t("authPage.nameLabel")}
              </Label>
              <Input
                id="name"
                name="name"
                required
                type="text"
                placeholder={t("authPage.namePlaceholder")}
              />
              {actionData &&
              "fieldErrors" in actionData &&
              actionData.fieldErrors?.name ? (
                <FormErrors
                  errors={translateAuthErrors(actionData.fieldErrors.name, t)}
                />
              ) : null}
            </div>
            <div className="flex flex-col items-start space-y-2">
              <Label
                htmlFor="email"
                className="flex flex-col items-start gap-1"
              >
                {t("authPage.emailLabel")}
              </Label>
              <Input
                id="email"
                name="email"
                required
                type="email"
                placeholder={t("authPage.emailPlaceholder")}
              />
              {actionData &&
              "fieldErrors" in actionData &&
              actionData.fieldErrors?.email ? (
                <FormErrors
                  errors={translateAuthErrors(actionData.fieldErrors.email, t)}
                />
              ) : null}
            </div>
            <div className="flex flex-col items-start space-y-2">
              <Label
                htmlFor="password"
                className="flex flex-col items-start gap-1"
              >
                {t("authPage.passwordLabel")}
                <small className="text-muted-foreground">
                  {t("authPage.passwordHint")}
                </small>
              </Label>
              <Input
                id="password"
                name="password"
                required
                type="password"
                placeholder={t("authPage.passwordPlaceholder")}
              />
              {actionData &&
              "fieldErrors" in actionData &&
              actionData.fieldErrors?.password ? (
                <FormErrors
                  errors={translateAuthErrors(
                    actionData.fieldErrors.password,
                    t,
                  )}
                />
              ) : null}
            </div>
            <div className="flex flex-col items-start space-y-2">
              <Label
                htmlFor="confirmPassword"
                className="flex flex-col items-start gap-1"
              >
                {t("authPage.confirmPasswordLabel")}
              </Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                required
                type="password"
                placeholder={t("authPage.confirmPasswordPlaceholder")}
              />
              {actionData &&
              "fieldErrors" in actionData &&
              actionData.fieldErrors?.confirmPassword ? (
                <FormErrors
                  errors={translateAuthErrors(
                    actionData.fieldErrors.confirmPassword,
                    t,
                  )}
                />
              ) : null}
            </div>
            <TurnstileWidget
              className="w-full min-w-0"
              onSuccess={(token) => setCaptchaToken(token)}
              onExpire={() => setCaptchaToken(null)}
              onError={() => setCaptchaToken(null)}
            />
            <FormButton
              label={t("authPage.createAccountButton")}
              className="w-full"
              disabled={!captchaToken}
            />
            {actionData && "error" in actionData && actionData.error ? (
              <FormErrors errors={translateAuthErrors([actionData.error], t)} />
            ) : null}

            <div className="flex min-w-0 items-start gap-2">
              <Checkbox id="marketing" name="marketing" />
              <Label
                htmlFor="marketing"
                className="text-muted-foreground min-w-0 flex-1 text-sm leading-relaxed break-words whitespace-normal"
              >
                {t("authPage.marketingLabel")}
              </Label>
            </div>
            <div className="flex min-w-0 items-start gap-2">
              <Checkbox id="terms" name="terms" checked />
              <Label
                htmlFor="terms"
                className="text-muted-foreground min-w-0 flex-1 text-sm leading-relaxed break-words whitespace-normal"
              >
                <span className="inline">
                  {t("authPage.termsPrefix")}{" "}
                  <Link
                    to="/legal/terms-of-service"
                    viewTransition
                    className="text-muted-foreground hover:text-foreground underline transition-colors"
                  >
                    {t("authPage.termsOfService")}
                  </Link>{" "}
                  {t("authPage.and")}{" "}
                  <Link
                    to="/legal/privacy-policy"
                    viewTransition
                    className="text-muted-foreground hover:text-foreground underline transition-colors"
                  >
                    {t("authPage.privacyPolicy")}
                  </Link>
                </span>
              </Label>
            </div>
            {actionData && "success" in actionData && actionData.success ? (
              <Alert className="bg-green-600/20 text-green-700 dark:bg-green-950/20 dark:text-green-600">
                <CheckCircle2Icon
                  className="size-4"
                  color="oklch(0.627 0.194 149.214)"
                />
                <AlertTitle>{t("authPage.accountCreatedTitle")}</AlertTitle>
                <AlertDescription className="text-green-700 dark:text-green-600">
                  {t("authPage.accountCreatedDescription")}
                </AlertDescription>
              </Alert>
            ) : null}
          </Form>
        </CardContent>
      </Card>
      <div className="flex w-full flex-col items-center justify-center px-2 text-sm">
        <p className="text-muted-foreground break-words text-center">
          {t("authPage.alreadyHaveAccountPrompt")}{" "}
          <Link
            to="/login"
            viewTransition
            data-testid="form-signin-link"
            className="text-muted-foreground hover:text-foreground text-underline underline transition-colors"
          >
            {t("authPage.signInLink")}
          </Link>
        </p>
      </div>
    </div>
  );
}
